import type { OptimizationStudy, SolverHistoryEntry } from '@minglun/domain';

export interface ResultComparisonGroup {
  id: string;
  name: string;
  entries: SolverHistoryEntry[];
}

const comparisonScope = (entry: SolverHistoryEntry): string =>
  entry.studyId ?? entry.studyName ?? 'unassigned';

export function resultStudyName(
  entry: SolverHistoryEntry,
  studies: OptimizationStudy[] | undefined,
): string {
  return studies?.find((study) => study.id === entry.studyId)?.name
    ?? entry.studyName
    ?? '未归属方案';
}

export function groupComparisonHistories(
  histories: SolverHistoryEntry[],
  studies: OptimizationStudy[] | undefined,
  activeStudyId: string | undefined,
): ResultComparisonGroup[] {
  const groups = new Map<string, ResultComparisonGroup>();
  for (const entry of histories) {
    const id = entry.studyId ?? 'unassigned';
    const existing = groups.get(id);
    if (existing) existing.entries.push(entry);
    else groups.set(id, { id, name: resultStudyName(entry, studies), entries: [entry] });
  }
  return [...groups.values()].sort((left, right) => {
    if (left.id === activeStudyId) return -1;
    if (right.id === activeStudyId) return 1;
    return left.name.localeCompare(right.name, 'zh-CN');
  });
}

export function defaultComparisonIds(
  histories: SolverHistoryEntry[],
  preferredId: string | undefined,
): string[] {
  if (histories.length < 2) return [];
  const comparison = histories.find((entry) => entry.id === preferredId) ?? histories[0]!;
  const ordered = histories.filter(entry => comparisonScope(entry) === comparisonScope(comparison))
    .sort((a,b) => b.createdAt.localeCompare(a.createdAt));
  const baseline = ordered[ordered.findIndex(entry => entry.id === comparison.id) + 1];
  return baseline ? [baseline.id, comparison.id] : [];
}

export function comparesDifferentStudies(entries: SolverHistoryEntry[]): boolean {
  return entries.length === 2 && comparisonScope(entries[0]!) !== comparisonScope(entries[1]!);
}
